import { Link, useLocation } from 'react-router-dom';
import { X, PanelLeftClose, PanelLeft } from 'lucide-react';
import { cn } from '@/lib/utils';
import { preloadRoute } from '@/lib/preload';
import { useAuth } from '@/hooks/useAuth';
import { canAccessPath } from '@/lib/permission-access';
import { menuGroups } from './menu-config';

interface SidebarProps {
  className?: string;
  style?: React.CSSProperties;
  isMobile?: boolean;
  onClose?: () => void;
  isCollapsed?: boolean;
  onToggleCollapse?: () => void;
}

export function Sidebar({
  className,
  style,
  isMobile = false,
  onClose,
  isCollapsed = false,
  onToggleCollapse,
}: SidebarProps) {
  const location = useLocation();
  const { permissions } = useAuth();

  const isActive = (path: string) => {
    return (
      location.pathname === path || location.pathname.startsWith(`${path}/`)
    );
  };

  const hasPathAccess = (path: string): boolean =>
    canAccessPath(path, permissions);

  const visibleMenuGroups = menuGroups
    .map((group) => ({
      ...group,
      items: group.items.filter(
        (item) => !item.disabled && hasPathAccess(item.path)
      ),
    }))
    .filter((group) => group.items.length > 0);

  // 获取版本号：统一使用构建注入版本（Git tag/CI VERSION）
  const getVersion = () => {
    if (typeof __APP_VERSION__ !== 'undefined') {
      return __APP_VERSION__;
    }
    return 'unknown';
  };

  // 获取 Git revision
  const getGitRevision = () => {
    // 优先使用 Vite 注入的常量
    if (typeof __APP_REVISION__ !== 'undefined') {
      return __APP_REVISION__;
    }
    return 'unknown';
  };

  return (
    <aside
      className={cn(
        'flex h-full flex-col border border-border bg-card transition-all duration-300',
        isCollapsed ? 'w-16' : 'w-64',
        isMobile && 'pt-2',
        className
      )}
      style={style}
      role="navigation"
      aria-label="主导航"
    >
      {/* Logo */}
      <div
        className={cn(
          'flex h-16 items-center border-b transition-all duration-300',
          isCollapsed ? 'justify-center px-3' : 'justify-between gap-2 px-6'
        )}
      >
        {isCollapsed ? (
          // 收起状态：只显示Logo图标
          <div
            className="flex h-8 w-8 items-center justify-center rounded-lg flex-shrink-0"
            style={{
              background: 'linear-gradient(135deg, #7bb8ff 0%, #3F3663 100%)',
            }}
          >
            <img
              src="/whale-logo-white.png"
              alt="WhaleHire"
              className="h-5 w-5"
            />
          </div>
        ) : (
          // 展开状态：显示完整Logo和标题
          <>
            <div className="flex items-center gap-2">
              <div
                className="flex h-8 w-8 items-center justify-center rounded-lg flex-shrink-0"
                style={{
                  background:
                    'linear-gradient(135deg, #7bb8ff 0%, #3F3663 100%)',
                }}
              >
                <img
                  src="/whale-logo-white.png"
                  alt="WhaleHire"
                  className="h-5 w-5"
                />
              </div>
              <div className="flex items-center gap-2 overflow-hidden">
                <span className="text-lg font-semibold whitespace-nowrap">
                  WhaleHire
                </span>
              </div>
            </div>
            {isMobile ? (
              <button
                type="button"
                className="rounded-md p-2 text-muted-foreground transition hover:bg-accent hover:text-accent-foreground flex-shrink-0"
                onClick={onClose}
                aria-label="关闭侧边栏"
              >
                <X className="h-4 w-4" />
              </button>
            ) : null}
          </>
        )}
      </div>

      {/* Navigation */}
      <div className="flex flex-1 flex-col overflow-hidden justify-between">
        <div className="scrollbar-thin scrollbar-thumb-muted-foreground/20 scrollbar-track-transparent -mr-1 flex-1 overflow-y-auto pr-1">
          <div className="pt-4">
            {visibleMenuGroups.map((group, groupIndex) => (
              <div
                key={group.id}
                className={cn(
                  isCollapsed ? 'px-2' : 'px-3',
                  groupIndex > 0 && 'mt-6'
                )}
              >
                {/* 主菜单标题 */}
                {!isCollapsed && groupIndex === 0 && (
                  <div className="mb-3 px-3 flex items-center justify-between">
                    <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider">
                      {group.label}
                    </span>
                    {/* 收起/展开按钮 - 仅桌面端显示 */}
                    {!isMobile && onToggleCollapse && (
                      <button
                        onClick={onToggleCollapse}
                        className="p-1.5 rounded-lg border border-border text-muted-foreground hover:bg-accent hover:text-accent-foreground hover:border-primary/50 transition-all"
                        title="收起侧边栏"
                      >
                        <PanelLeftClose className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                )}
                {!isCollapsed && groupIndex > 0 && (
                  <div className="mb-3 px-3">
                    <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider">
                      {group.label}
                    </span>
                  </div>
                )}
                {/* 收起状态下的展开按钮 - 显示在第一个菜单组 */}
                {isCollapsed &&
                  groupIndex === 0 &&
                  !isMobile &&
                  onToggleCollapse && (
                    <div className="mb-3 flex justify-center">
                      <button
                        onClick={onToggleCollapse}
                        className="p-1.5 rounded-lg border border-border text-muted-foreground hover:bg-accent hover:text-accent-foreground hover:border-primary/50 transition-all"
                        title="展开侧边栏"
                      >
                        <PanelLeft className="h-4 w-4" />
                      </button>
                    </div>
                  )}

                {/* 子菜单项 */}
                <nav className="sidebar-nav">
                  {group.items.map((item) => {
                    const Icon = item.icon;
                    return (
                      <Link
                        key={item.id}
                        to={item.path}
                        className={cn(
                          'sidebar-nav-item',
                          isActive(item.path) && 'active',
                          isCollapsed && 'justify-center px-2'
                        )}
                        onClick={isMobile ? onClose : undefined}
                        onMouseEnter={() => preloadRoute(item.path)}
                        title={isCollapsed ? item.label : undefined}
                      >
                        <Icon className="h-4 w-4 flex-shrink-0" />
                        {!isCollapsed && <span>{item.label}</span>}
                      </Link>
                    );
                  })}
                </nav>
              </div>
            ))}
          </div>
        </div>

        {/* Footer */}
        {!isCollapsed && (
          <div className="border-t mt-auto px-4 py-3">
            <div className="rounded-lg border border-[#D9E2F5] bg-[#F8FAFF] px-3 py-2 space-y-1">
              <div className="text-xs text-gray-500">
                版本号：{getVersion()}
              </div>
              <div className="text-xs text-gray-400 font-mono">
                rev: {getGitRevision()}
              </div>
            </div>
          </div>
        )}
      </div>
    </aside>
  );
}
